'use client'

import * as ToggleGroupPrimitive from '@radix-ui/react-toggle-group'
import { type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import {
  toggleIconVariants,
  toggleTextVariants,
  toggleVariants,
} from '@/components/ui/toggle'
import { cn } from '@/lib/utils'

const ToggleGroupContext = React.createContext<
  VariantProps<typeof toggleVariants>
>({
  size: 'default',
  variant: 'default',
})

const ToggleGroup = React.forwardRef<
  React.ElementRef<typeof ToggleGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof ToggleGroupPrimitive.Root> &
    VariantProps<typeof toggleVariants>
>(({ className, variant, size, children, ...props }, ref) => (
  <ToggleGroupPrimitive.Root
    ref={ref}
    className={cn('flex items-center justify-center gap-2', className)}
    {...props}
  >
    <ToggleGroupContext.Provider value={{ variant, size }}>
      {children}
    </ToggleGroupContext.Provider>
  </ToggleGroupPrimitive.Root>
))

ToggleGroup.displayName = ToggleGroupPrimitive.Root.displayName

interface ToggleGroupItemProps
  extends React.ComponentPropsWithoutRef<typeof ToggleGroupPrimitive.Item>,
    VariantProps<typeof toggleVariants> {
  icon?: React.ReactNode
  text?: string
}

const ToggleGroupItem = React.forwardRef<
  React.ElementRef<typeof ToggleGroupPrimitive.Item>,
  ToggleGroupItemProps
>(({ className, children, variant, size, icon, text, ...props }, ref) => {
  const context = React.useContext(ToggleGroupContext)

  const contextVariant = context.variant || variant
  const contextSize = context.size || size

  if (contextVariant === 'fintalk' && (icon || text)) {
    return (
      <ToggleGroupPrimitive.Item
        ref={ref}
        className={cn(
          toggleVariants({
            variant: contextVariant,
            size: contextSize,
          }),
          'group',
          className,
        )}
        {...props}
      >
        {icon && (
          <div
            className={cn(
              toggleIconVariants({ variant: contextVariant }),
              'group-data-[state=on]:bg-gradient-to-b group-data-[state=on]:from-[#393760] group-data-[state=on]:to-[#5e619d]',
              'group-disabled:bg-[rgba(86,83,134,0.17)]',
              '[&_svg]:h-4 [&_svg]:w-4 [&_svg]:shrink-0',
              '[&_svg]:stroke-[#393760] [&_svg]:stroke-[1.5]',
              'group-data-[state=on]:[&_svg]:stroke-white',
              'group-disabled:[&_svg]:stroke-[#565386]',
            )}
          >
            {icon}
          </div>
        )}
        {text && (
          <p
            className={cn(
              toggleTextVariants({ variant: contextVariant }),
              'group-data-[state=on]:text-[#333333]',
              'group-disabled:text-[rgba(86,83,134,0.17)]',
            )}
          >
            {text}
          </p>
        )}
        {children}
      </ToggleGroupPrimitive.Item>
    )
  }

  return (
    <ToggleGroupPrimitive.Item
      ref={ref}
      className={cn(
        toggleVariants({
          variant: contextVariant,
          size: contextSize,
        }),
        className,
      )}
      {...props}
    >
      {children}
    </ToggleGroupPrimitive.Item>
  )
})

ToggleGroupItem.displayName = ToggleGroupPrimitive.Item.displayName

export { ToggleGroup, ToggleGroupItem }
